import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, addDoc, serverTimestamp, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useTranslation } from '../../hooks/useTranslation';
import * as Icons from 'lucide-react';

export default function TicketDetails() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [ticket, setTicket] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const fetchTicketAndReplies = async () => {
      try {
        // Fetch ticket details
        const ticketDoc = await getDoc(doc(db, 'tickets', ticketId));
        if (ticketDoc.exists()) {
          setTicket({ id: ticketDoc.id, ...ticketDoc.data() });
        }

        // Fetch replies
        const repliesRef = collection(db, 'ticket_replies');
        const q = query(
          repliesRef,
          where('ticket_id', '==', ticketId),
          orderBy('created_at', 'asc')
        );
        const repliesSnapshot = await getDocs(q);
        setReplies(repliesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        console.error('Error fetching ticket details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTicketAndReplies();
  }, [ticketId]);

  const handleStatusChange = async (newStatus) => {
    try {
      await updateDoc(doc(db, 'tickets', ticketId), {
        status: newStatus,
        updated_at: serverTimestamp()
      });
      setTicket(prev => ({ ...prev, status: newStatus }));
    } catch (error) {
      console.error('Error updating ticket status:', error);
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;

    setSending(true);
    try {
      const reply = {
        ticket_id: ticketId,
        message: replyText,
        created_at: serverTimestamp(),
        created_by: 'admin', // TODO: Use actual admin info
        is_admin: true
      };

      const replyRef = await addDoc(collection(db, 'ticket_replies'), reply);
      setReplies(prev => [...prev, { id: replyRef.id, ...reply }]);
      setReplyText('');

      // Update ticket status and timestamp
      await updateDoc(doc(db, 'tickets', ticketId), {
        status: 'Pending',
        updated_at: serverTimestamp()
      });
    } catch (error) {
      console.error('Error sending reply:', error);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Icons.Loader className="animate-spin h-8 w-8 text-gray-500" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">
            {t('tickets.details.notFound')}
          </h2>
          <button
            onClick={() => navigate('/admin/tickets')}
            className="mt-4 text-indigo-600 hover:text-indigo-900"
          >
            {t('common.back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t('tickets.details.ticketNumber')}{ticket.id.slice(-8)} - {ticket.subject}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              {t('tickets.details.openedBy')} {ticket.customer_name} - {
                ticket.created_at?.seconds ? 
                new Date(ticket.created_at.seconds * 1000).toLocaleString() : 
                t('tickets.details.justNow')
              }
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <select
              value={ticket.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              aria-label={t('tickets.details.changeStatus')}
            >
              <option value="Open">{t('tickets.status.open')}</option>
              <option value="Pending">{t('tickets.status.pending')}</option>
              <option value="Closed">{t('tickets.status.closed')}</option>
              <option value="Resolved">{t('tickets.status.resolved')}</option>
            </select>
            <button
              onClick={() => navigate('/admin/tickets')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <Icons.ArrowLeft className="h-4 w-4 mr-2" />
              {t('common.back')}
            </button>
          </div>
        </div>
      </div>

      {/* Ticket Info */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-100 mb-6">
        <div className="p-6">
          <h2 className="text-sm font-medium text-gray-700 mb-2">
            {t('tickets.details.mainMessage')}
          </h2>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                <Icons.User className="h-6 w-6 text-gray-500" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-gray-900 whitespace-pre-wrap">
                {ticket.message}
              </div>
              <div className="mt-2 flex items-center space-x-4">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  ticket.priority === 'High' ? 'bg-red-100 text-red-800' :
                  ticket.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-green-100 text-green-800'
                }`}>
                  {ticket.priority}
                </span>
                <span className="text-sm text-gray-500">
                  {t('tickets.details.department')}: {ticket.department}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Replies */}
      <div className="space-y-6">
        {replies.map((reply) => (
          <div 
            key={reply.id}
            className={`bg-white shadow-sm rounded-lg border border-gray-100 ${
              reply.is_admin ? 'ml-12' : ''
            }`}
          >
            <div className="p-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                    reply.is_admin ? 'bg-indigo-100' : 'bg-gray-200'
                  }`}>
                    {reply.is_admin ? (
                      <Icons.Shield className="h-6 w-6 text-indigo-600" />
                    ) : (
                      <Icons.User className="h-6 w-6 text-gray-500" />
                    )}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    {reply.is_admin ? t('tickets.details.supportTeam') : ticket.customer_name}
                  </p>
                  <p className="text-sm text-gray-500">
                    {reply.created_at?.seconds ? 
                      new Date(reply.created_at.seconds * 1000).toLocaleString() : 
                      t('tickets.details.justNow')}
                  </p>
                  <div className="mt-2 text-sm text-gray-900 whitespace-pre-wrap">
                    {reply.message}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Reply Form */}
      <div className="mt-6">
        <form onSubmit={handleReply} className="bg-white shadow-sm rounded-lg border border-gray-100">
          <div className="p-6">
            <label htmlFor="reply" className="sr-only">
              {t('tickets.details.reply')}
            </label>
            <textarea
              id="reply"
              rows={4}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder={t('tickets.details.replyPlaceholder')}
            />
            <div className="mt-4 flex justify-end">
              <button
                type="submit"
                disabled={sending || !replyText.trim()}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {sending && <Icons.Loader className="animate-spin -ml-1 mr-2 h-4 w-4" />}
                {t('tickets.details.sendReply')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
} 